import React from "react";
import {
  IdentificationIcon,
  CubeIcon,
  QrCodeIcon,
  CalendarIcon,
  TagIcon,
  ScaleIcon,
  ArrowUpOnSquareIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ChatBubbleBottomCenterTextIcon,
  UserIcon,
  PhoneIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

const AssetOutwardView = ({ currentRecord }) => {
  const renderRow = (icon, label, value, isAlternate = false) => {
    // Format value if it's null or undefined
    const displayValue = value === null || value === undefined ? "-" : value;
    
    return (
      <tr
        className={`${
          isAlternate ? "bg-gray-50" : "bg-white"
        } hover:bg-gray-100 transition-colors duration-150 ease-in-out`}
      >
        <td className="py-3 pl-4 pr-2 w-1/2">
          <div className="flex items-center">
            {React.createElement(icon, {
              className: "h-5 w-5 text-indigo-500 mr-2 flex-shrink-0",
            })}
            <span className="font-medium text-gray-800 text-sm sm:text-base">{label}</span>
          </div>
        </td>
        <td className="py-3 pl-2 pr-4 text-sm text-right text-gray-700 break-words w-1/2">
          {displayValue}
        </td>
      </tr>
    );
  };
  
  // Helper function to format currency values
  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR' 
    }).format(value);
  };

  const outwardOptions = [
    { value: 1, label: "Rental" },
    { value: 2, label: "Donated" },
    { value: 3, label: "Transferred" },
  ];

  return (
    <div className="w-full h-full">
      <div className="overflow-hidden bg-white shadow-lg rounded-lg border border-gray-100 w-full">
        <div className="px-4 py-4 bg-gradient-to-r from-indigo-50 to-white border-b border-indigo-100">
          <div className="flex items-center">
            <InformationCircleIcon className="h-5 w-5 text-indigo-600 mr-2" />
            <h3 className="text-lg font-semibold leading-6 text-indigo-800">
              Asset Outward Details
            </h3>
          </div>
          <p className="mt-1 text-xs text-gray-600">
            Detailed information about the outward asset.
          </p>
        </div>
        <div className="overflow-x-auto">
          <div className="w-full align-middle">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <tbody className="bg-white divide-y divide-gray-200">
                {renderRow(IdentificationIcon, "ID", currentRecord?.id)}
                {renderRow(
                  IdentificationIcon,
                  "Issued ID",
                  currentRecord?.issued_id,
                  true
                )}
                {renderRow(CubeIcon, "Asset Name", currentRecord?.asset_name)}
                {renderRow(
                  QrCodeIcon,
                  "Item Code",
                  currentRecord?.item_code,
                  true
                )}
                {renderRow(
                  CalendarIcon,
                  "Issued Date",
                  currentRecord?.issued_date ? new Date(currentRecord.issued_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : "-",
                  true
                )}
                {renderRow(ScaleIcon, "Quantity", 
                  currentRecord?.quantity ? `${currentRecord.quantity} ${currentRecord?.unit || 'units'}` : "-", 
                  true
                )}
                {renderRow(
                  ArrowUpOnSquareIcon,
                  "Issue Type",
                  (() => {
                    try {
                      // Try to parse as JSON first
                      return JSON.parse(currentRecord?.issue_type).label;
                    } catch (error) {
                      // If it's a number, try to match with outwardOptions
                      if (!isNaN(currentRecord?.issue_type)) {
                        const option = outwardOptions.find(opt => opt.value == currentRecord?.issue_type);
                        return option ? option.label : currentRecord?.issue_type || "-";
                      }
                      // If parsing fails, return the raw value
                      return currentRecord?.issue_type || "-";
                    }
                  })()
                )}
                {renderRow(
                  CurrencyDollarIcon,
                  "Issued Rate",
                  formatCurrency(currentRecord?.issued_rate),
                  true
                )}
                {renderRow(
                  DocumentTextIcon,
                  "Issued Description",
                  currentRecord?.issued_description || "-"
                )}
                {renderRow(UserIcon, "Issuer", currentRecord?.issuer_id || "-", true)}
                {renderRow(UserIcon, "Allocated To", currentRecord?.allocated_to || "-")}
                {renderRow(
                  PhoneIcon,
                  "Allocated Mobile",
                  currentRecord?.allocated_mobile || "-",
                  true
                )}
                {renderRow(
                  CalendarIcon,
                  "Expected Date of Return",
                  currentRecord?.expected_date_of_return ? new Date(currentRecord.expected_date_of_return).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : "-"
                )}
                {renderRow(
                  CheckCircleIcon,
                  "Status",
                  (() => {
                    if (!currentRecord?.status) return "-";
                    
                    // Determine the status string to display
                    const statusText = String(currentRecord.status);
                    
                    // Determine the appropriate color class
                    let colorClass = 'bg-gray-100 text-gray-800';
                    const statusLower = statusText.toLowerCase();
                    
                    if (statusLower === 'active' || statusLower === 'issued') {
                      colorClass = 'bg-green-100 text-green-800';
                    } else if (statusLower === 'pending') {
                      colorClass = 'bg-yellow-100 text-yellow-800';
                    } else if (statusLower === 'returned') {
                      colorClass = 'bg-blue-100 text-blue-800';
                    }
                    
                    return (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                        {statusText}
                      </span>
                    );
                  })(),
                  true
                )}
                {renderRow(
                  ChatBubbleBottomCenterTextIcon,
                  "Comments",
                  currentRecord?.comments || "-"
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-right">
          <p className="text-xs text-gray-500">Last updated: {currentRecord?.updated_at ? new Date(currentRecord.updated_at).toLocaleString() : 'N/A'}</p>
        </div>
      </div>
    </div>
  );
};

export default AssetOutwardView;
